// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - DUST DETECTION UTILITY V3
// Smart filtering with object type detection and improved logic
// UPDATED: Mar 2026 - Three-tier safe token check + aggregate dust detection
//
// KEY PRINCIPLE: Dust is evaluated at the COIN TYPE level (total balance
// across all objects of that type), not per individual object.
// 67 CETUS objects worth $50 total → all protected.
// 67 CETUS objects worth $0.001 total → all shown for cleanup.
// ═══════════════════════════════════════════════════════════════════════════

import { extractCoinType, getCoinMetadata } from './coinMetadata';
import { getSafeTokenResult, MIN_SYMBOL_MATCH_USD } from './safeTokens';

// ═══════════════════════════════════════════════════════════════════════════
// DUST THRESHOLDS
// ═══════════════════════════════════════════════════════════════════════════

const DUST_THRESHOLDS = {
  TOKEN_AMOUNT: 0.01,  // Aggregate < 0.01 tokens = dust
  USD_VALUE: 0.10,     // Aggregate < $0.10 USD = dust
};

// ═══════════════════════════════════════════════════════════════════════════
// VALUABLE OBJECT TYPE DETECTION
// ═══════════════════════════════════════════════════════════════════════════

export function isValuableObjectType(objectType) {
  if (!objectType) return false;

  const type = objectType.toUpperCase();

  if (type.includes('::LP<') ||
      type.includes('::LIQUIDITYPOOL') ||
      type.includes('POOL::LP')) return true;

  if (type.includes('::STAKEDSUI') ||
      type.includes('::STAKE') ||
      type.includes('STAKING_POOL::') ||
      type.includes('::STAKINGPOOL')) return true;

  if (type.includes('::POSITION') ||
      type.includes('::CLMM::') ||
      type.includes('::ORDER')) return true;

  if (type.includes('::VAULT') ||
      type.includes('::RECEIPT') ||
      type.includes('::DEPOSIT')) return true;

  if (type.includes('::OBLIGATION') ||
      type.includes('::COLLATERAL') ||
      type.includes('::BORROW')) return true;

  return false;
}

export function isNFT(obj) {
  const type = obj?.data?.type || '';
  if (obj?.data?.display?.data) return true;
  const coinType = extractCoinType(type);
  if (!coinType && !isValuableObjectType(type)) return true;
  return false;
}

// ═══════════════════════════════════════════════════════════════════════════
// AGGREGATE BALANCE MAP
// Pre-compute total balance per coin type across ALL objects in the batch.
// This is what makes dust detection correct for multi-object coin types.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build a map of { coinType → { totalRawBalance, decimals } }
 * Fetches metadata once per unique coin type (cached by coinMetadata.js).
 */
async function buildAggregateBalanceMap(objects, suiClient) {
  const map = new Map();

  await Promise.all(objects.map(async (obj) => {
    const type = obj?.data?.type;
    if (!type) return;

    const coinType = extractCoinType(type);
    if (!coinType) return;

    const rawBalance = obj.data?.content?.fields?.balance;
    if (!rawBalance) return;

    const balanceNum = typeof rawBalance === 'string' ? Number(rawBalance) : rawBalance;
    if (balanceNum <= 0) return;

    if (!map.has(coinType)) {
      let decimals = 9;
      try {
        const metadata = await getCoinMetadata(suiClient, coinType);
        decimals = metadata?.decimals ?? 9;
      } catch { /* use default 9 */ }
      map.set(coinType, { totalRawBalance: 0, decimals });
    }

    map.get(coinType).totalRawBalance += balanceNum;
  }));

  return map;
}

/**
 * Check if a coin type's AGGREGATE balance is dust.
 * @param {string} coinType
 * @param {Map} aggregateMap
 * @param {number} tokenUsdPrice - price per 1 whole token in USD
 */
function isAggregateDust(coinType, aggregateMap, tokenUsdPrice = 0) {
  const entry = aggregateMap.get(coinType);
  if (!entry || entry.totalRawBalance <= 0) return true;

  const totalTokens = entry.totalRawBalance / Math.pow(10, entry.decimals);

  // Token amount gate
  if (totalTokens < DUST_THRESHOLDS.TOKEN_AMOUNT) return true;

  // USD value gate (only when price is available)
  if (tokenUsdPrice > 0) {
    const totalUsd = totalTokens * tokenUsdPrice;
    if (totalUsd < DUST_THRESHOLDS.USD_VALUE) return true;
  }

  return false;
}

// ═══════════════════════════════════════════════════════════════════════════
// PER-OBJECT DUST (legacy / edge cases without aggregate map)
// ═══════════════════════════════════════════════════════════════════════════

export async function isDustAmount(obj, suiClient) {
  const type = obj?.data?.type;
  if (!type) return false;

  const coinType = extractCoinType(type);
  if (!coinType) return false;

  const rawBalance = obj.data?.content?.fields?.balance;
  if (!rawBalance) return true;

  const balanceNum = typeof rawBalance === 'string' ? Number(rawBalance) : rawBalance;
  if (balanceNum === 0) return true;

  try {
    const metadata = await getCoinMetadata(suiClient, coinType);
    const decimals = metadata?.decimals || 9;
    const actualAmount = balanceNum / Math.pow(10, decimals);
    return actualAmount < DUST_THRESHOLDS.TOKEN_AMOUNT;
  } catch {
    return true;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// CORE FILTER LOGIC (internal, uses aggregate map)
// ═══════════════════════════════════════════════════════════════════════════

async function shouldShowForCleanupInternal(
  obj, suiClient, distilPackageId,
  aggregateMap, tokenUsdPrice = 0, objectUsdValue = 0
) {
  const type = obj?.data?.type || '';

  // ─── Hard exclusions ──────────────────────────────────────────────────

  if (type === '0x2::coin::Coin<0x2::sui::SUI>') {
    return { shouldShow: false, category: 'native_sui', reason: 'Native SUI never shown' };
  }

  if (distilPackageId && type.includes(distilPackageId)) {
    return { shouldShow: false, category: 'distil_object', reason: 'Protocol object' };
  }

  if (!obj.data?.objectId) {
    return { shouldShow: false, category: 'invalid', reason: 'Missing object ID' };
  }

  if (isValuableObjectType(type)) {
    return { shouldShow: false, category: 'valuable_object', reason: 'Active capital - too risky' };
  }

  // ─── Three-tier safe token check ──────────────────────────────────────

  const safeResult = getSafeTokenResult(type);

  if (safeResult.isSafe) {
    const coinType = extractCoinType(type);

    // Always evaluate dust at aggregate level
    const dustCheck = coinType
      ? isAggregateDust(coinType, aggregateMap, tokenUsdPrice)
      : await isDustAmount(obj, suiClient); // non-coin fallback

    if (dustCheck) {
      return {
        shouldShow: true,
        category: 'safe_dust',
        reason: 'Known token but total balance is dust',
      };
    }

    // Not dust — check tier
    if (!safeResult.requiresValueGate) {
      // Tier 1 (verified address) or Tier 2 (partner) — always protect
      const label = safeResult.tier === 'partner' ? 'Partner token 👑' : 'Verified address';
      return { shouldShow: false, category: 'safe_value', reason: `${label} with value` };
    }

    // Tier 3 (symbol match only) — need aggregate USD value confirmation
    let aggregateUsd = objectUsdValue;
    if (coinType && aggregateMap.has(coinType) && tokenUsdPrice > 0) {
      const { totalRawBalance, decimals } = aggregateMap.get(coinType);
      aggregateUsd = (totalRawBalance / Math.pow(10, decimals)) * tokenUsdPrice;
    }

    if (aggregateUsd >= MIN_SYMBOL_MATCH_USD) {
      return { shouldShow: false, category: 'safe_value', reason: 'Symbol match with real value' };
    }

    // Symbol matches but no confirmed value — surface for cleanup with warning
    return {
      shouldShow: true,
      category: 'unverified_symbol',
      reason: 'Symbol match but unverified — check before deleting',
    };
  }

  // ─── USD value gate — auto-protect anything worth > $0.50 ─────────────
  // Catches tokens that aren't in safeTokens list but have real value.
  // Runs BEFORE the NFT/unknown fallthrough so nothing valuable slips through.

  const coinType = extractCoinType(type);
  if (coinType && aggregateMap.has(coinType) && tokenUsdPrice > 0) {
    const { totalRawBalance, decimals } = aggregateMap.get(coinType);
    const aggregateUsd = (totalRawBalance / Math.pow(10, decimals)) * tokenUsdPrice;
    if (aggregateUsd > 0.50) {
      return {
        shouldShow: false,
        category: 'safe_value',
        reason: `Token has value ($${aggregateUsd.toFixed(2)}) — auto-protected`,
      };
    }
  }

  // ─── NFTs & unknown ───────────────────────────────────────────────────

  if (isNFT(obj)) {
    return { shouldShow: true, category: 'nft', reason: 'NFT - verify before deleting' };
  }

  return { shouldShow: true, category: 'unknown', reason: 'Unknown token - likely spam' };
}

// ═══════════════════════════════════════════════════════════════════════════
// PUBLIC SINGLE-OBJECT CHECK (legacy / external callers)
// ═══════════════════════════════════════════════════════════════════════════

export async function shouldShowForCleanup(obj, suiClient, distilPackageId, usdValue = 0) {
  const aggregateMap = await buildAggregateBalanceMap([obj], suiClient);
  return shouldShowForCleanupInternal(obj, suiClient, distilPackageId, aggregateMap, 0, usdValue);
}

// ═══════════════════════════════════════════════════════════════════════════
// BATCH FILTER — main entry point from App.jsx
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Builds the aggregate balance map ONCE across the full batch, then filters.
 * This is the only correct way to handle multi-object coin types.
 *
 * Objects may carry:
 *   .tokenPrice  — USD price per whole token (from price feed)
 *   .usdValue    — USD value of this specific object
 */
export async function filterObjectsForCleanup(objects, suiClient, distilPackageId) {
  if (!objects || objects.length === 0) {
    return { filtered: [], stats: { total: 0, shown: 0, categories: {} } };
  }

  // Single pass: build aggregate map for the entire batch
  const aggregateMap = await buildAggregateBalanceMap(objects, suiClient);

  const results = await Promise.all(
    objects.map(async (obj) => {
      const result = await shouldShowForCleanupInternal(
        obj,
        suiClient,
        distilPackageId,
        aggregateMap,
        obj.tokenPrice || 0,
        obj.usdValue || 0
      );
      return { obj, ...result };
    })
  );

  const filtered = results
    .filter(r => r.shouldShow)
    .map(r => ({
      ...r.obj,
      filterCategory: r.category,
      filterReason: r.reason,
    }));

  const stats = {
    total: objects.length,
    shown: filtered.length,
    hidden: objects.length - filtered.length,
    categories: {},
  };

  results.forEach(r => {
    stats.categories[r.category] = (stats.categories[r.category] || 0) + 1;
  });

  return { filtered, stats };
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

export function getFilterStats(allObjects, filteredObjects) {
  return {
    total: allObjects.length,
    shown: filteredObjects.length,
    hidden: allObjects.length - filteredObjects.length,
    percentageShown: ((filteredObjects.length / allObjects.length) * 100).toFixed(1),
  };
}

export function updateDustThresholds(tokenAmount, usdValue) {
  if (tokenAmount !== undefined) DUST_THRESHOLDS.TOKEN_AMOUNT = tokenAmount;
  if (usdValue !== undefined) DUST_THRESHOLDS.USD_VALUE = usdValue;
}

export function getDustThresholds() {
  return { ...DUST_THRESHOLDS };
}

export default {
  isDustAmount,
  shouldShowForCleanup,
  filterObjectsForCleanup,
  getFilterStats,
  updateDustThresholds,
  getDustThresholds,
  isValuableObjectType,
  isNFT,
};
